import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getCourses, getTeachers } from '../services/api';
import CourseCard from '../components/CourseCard';
import { useScrollReveal } from '../hooks/useScrollReveal';
import './SubjectPage.css';

/**
 * SubjectPage — /subjects/:slug
 *
 * Slug examples:
 *   رياضيات                      → subject only
 *   رياضيات-الصف-الثالث-الابتدائي → subject + grade
 *
 * Renders: meta tags, OG tags, JSON-LD, breadcrumb, courses grid, related teachers.
 */
export default function SubjectPage() {
  const { slug }  = useParams();
  const pageRef   = useScrollReveal();

  // Parse slug
  const parts     = decodeURIComponent(slug).split('-');
  const gradeIdx  = parts.findIndex(p => /^(الصف|السنة|KG|ثانوي|إعدادي|ابتدائي|جامعي)/.test(p));
  const subject   = (gradeIdx > 0 ? parts.slice(0, gradeIdx) : parts).join(' ');
  const grade     = gradeIdx > 0 ? parts.slice(gradeIdx).join(' ') : '';

  const pageTitle = grade
    ? `كورسات ${subject} — ${grade} | مدرسك`
    : `كورسات ${subject} | مدرسك`;
  const metaDesc  = grade
    ? `تعلم ${subject} لـ${grade} مع أفضل المدرسين المصريين. دروس مسجلة، اختبارات، وملفات قابلة للتحميل على منصة مدرسك.`
    : `تصفح أفضل كورسات ${subject} على منصة مدرسك مع مدرسين محترفين.`;

  const [courses,  setCourses]  = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading,  setLoading]  = useState(true);

  // ── Update document head ──────────────────────────────────────────────────
  useEffect(() => {
    document.title = pageTitle;
    const setMeta = (name, content, prop = false) => {
      const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(sel);
      if (!el) {
        el = document.createElement('meta');
        el[prop ? 'property' : 'name'] = name;
        document.head.appendChild(el);
      }
      el.content = content;
    };
    setMeta('description', metaDesc);
    setMeta('og:title',       pageTitle,                   true);
    setMeta('og:description', metaDesc,                    true);
    setMeta('og:type',        'website',                   true);
    setMeta('og:url',         window.location.href,        true);
    setMeta('twitter:card',        'summary_large_image');
    setMeta('twitter:title',       pageTitle);
    setMeta('twitter:description', metaDesc);
    return () => { document.title = 'مدرسك'; };
  }, [pageTitle, metaDesc]);

  // ── Load data ─────────────────────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    const params = { subject };
    if (grade) params.grade = grade;

    Promise.all([
      getCourses({ ...params, limit: 12 }),
      getTeachers({ subject }),
    ])
      .then(([cr, tr]) => {
        setCourses(cr.data.courses || []);
        setTeachers((tr.data.teachers || []).slice(0, 6));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [subject, grade]); // eslint-disable-line

  // ── JSON-LD ───────────────────────────────────────────────────────────────
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: pageTitle,
    description: metaDesc,
    url: window.location.href,
    numberOfItems: courses.length,
    itemListElement: courses.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'Course',
        name:    c.title,
        description: c.description || '',
        provider: { '@type': 'Organization', name: 'مدرسك' },
        offers:  { '@type': 'Offer', price: c.price, priceCurrency: 'EGP' },
      },
    })),
  };

  return (
    <div className="subject-page page-enter" ref={pageRef}>

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      {/* Header */}
      <div className="sp-header">
        <nav className="sp-breadcrumb">
          <Link to="/">الرئيسية</Link>
          <span>/</span>
          <Link to="/courses">الكورسات</Link>
          <span>/</span>
          <Link to={`/courses?subject=${encodeURIComponent(subject)}`}>{subject}</Link>
          {grade && <><span>/</span><span>{grade}</span></>}
        </nav>

        <h1>
          {grade
            ? <>{subject} — <span className="sp-grade">{grade}</span></>
            : subject
          }
        </h1>
        <p className="sp-desc">{metaDesc}</p>

        <div className="sp-stats">
          <span>📚 {courses.length} كورس</span>
          <span>👨‍🏫 {teachers.length} مدرّس</span>
        </div>
      </div>

      {/* Body */}
      <div className="sp-body">

        {/* Courses */}
        <section>
          <h2>الكورسات المتاحة</h2>
          {loading ? (
            <div className="sp-loading"><div className="spinner" /></div>
          ) : courses.length === 0 ? (
            <div className="sp-empty">
              <span>📭</span>
              <p>لا توجد كورسات لهذه المادة بعد</p>
              <Link to="/courses" className="btn btn-outline">تصفح كل الكورسات</Link>
            </div>
          ) : (
            <div className="sp-courses-grid">
              {courses.map((c, i) => (
                <div key={c.id} data-reveal="up" data-delay={`${i * 40}`}>
                  <CourseCard course={c} />
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Related teachers */}
        {teachers.length > 0 && (
          <section className="sp-section">
            <h2>مدرسو {subject}</h2>
            <div className="sp-teachers">
              {teachers.map(t => (
                <Link key={t.uid} to={`/teachers/${t.username || t.uid}`} className="sp-teacher-card">
                  <div className="sp-teacher-avatar">
                    {t.avatar
                      ? <img src={t.avatar} alt={t.name} />
                      : <span>{t.name?.[0]}</span>
                    }
                  </div>
                  <div className="sp-teacher-name">{t.name}</div>
                  {t.rating > 0 && <div className="sp-teacher-rating">⭐ {t.rating.toFixed(1)}</div>}
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Internal linking — related grade pages */}
        <section className="sp-section">
          <h2>مواد وصفوف مرتبطة</h2>
          <div className="sp-related">
            {['الصف الأول الابتدائي', 'الصف الثاني الابتدائي', 'الصف الثالث الابتدائي',
              'الصف الأول الإعدادي', 'الصف الثاني الإعدادي', 'الصف الثالث الإعدادي',
              'الصف الأول الثانوي', 'الصف الثاني الثانوي', 'الصف الثالث الثانوي'].map(g => (
              <Link
                key={g}
                to={`/subjects/${encodeURIComponent(subject + '-' + g)}`}
                className="sp-related-link"
              >
                {subject} — {g}
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
